import axios from 'axios';
import { httpClientLeadService } from '../utils/http.client';
import publicFormClient from '../utils/public-form.http.client';
import { API_LEAD_SERVICE_CONFIG } from '../config/api.config';
import { tokenManager } from '../utils/token.manager';

const BASE = '/v1/screen-recordings';

export const screenRecordingService = {
  /**
   * Upload a recorded video blob to the backend server.
   * @param {Blob} blob - Recorded video (webm/mp4)
   * @param {{ publicSession?: boolean, publicLinkToken?: string, fileName?: string, title?: string, pageUrl?: string, durationMs?: number }} meta
   * @param {(percent: number) => void} [onProgress]
   */
  upload: async (blob, meta = {}, onProgress) => {
    // Recorder codec lists can contain unquoted commas, which multipart parsers
    // reject as an invalid Content-Type (falling back to text/plain).
    // Only send the container type; slicing preserves the encoded media bytes.
    const mimeType = blob.type.split(';')[0].trim().toLowerCase();
    const extensions = { 'video/webm': 'webm', 'video/mp4': 'mp4', 'video/x-matroska': 'mkv' };
    const ext = extensions[mimeType];
    if (!ext) throw new Error(`Unsupported recording type: ${blob.type || 'unknown'}`);
    const uploadBlob = blob.slice(0, blob.size, mimeType);
    const baseName = (meta.fileName || `screen-recording-${Date.now()}`).replace(/\.(webm|mp4|mkv)$/i, '');
    const formData = new FormData();
    // Text fields first so multer can read file_name while choosing the on-disk name.
    formData.append('file_name', baseName);
    if (meta.title) formData.append('title', meta.title);
    // Public URLs contain a bearer link; do not persist it in recording metadata.
    if (meta.pageUrl && !meta.publicSession) formData.append('page_url', meta.pageUrl);
    if (meta.durationMs) formData.append('duration_ms', String(Math.round(meta.durationMs)));
    formData.append('video', uploadBlob, `${baseName}.${ext}`);

    // DUQ links may use the regular login session instead of a link session.
    const useLinkSession = meta.publicSession && !!sessionStorage.getItem('link_access_token');
    const usePublicLink = meta.publicSession && !!meta.publicLinkToken;
    if (!usePublicLink && !useLinkSession && !tokenManager.getToken()) {
      throw new Error('No active session available to save this recording. Please sign in, then retry Save.');
    }
    const client = usePublicLink ? axios : useLinkSession ? publicFormClient : httpClientLeadService;
    const url = usePublicLink ? `${API_LEAD_SERVICE_CONFIG.BASE_LEAD_SERVICE_URL}${BASE}/upload` : `${BASE}/upload`;
    const response = await client.post(url, formData, {
      headers: { 'Content-Type': undefined, ...(usePublicLink ? { Authorization: `PublicForm ${meta.publicLinkToken}` } : {}) },
      timeout: 0, // large uploads; do not apply the default 60s timeout
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },

  /** List the current user's recordings, newest first. */
  list: async () => {
    const response = await httpClientLeadService.get(BASE);
    return response.data?.data || [];
  },

  /** Delete a recording by id. */
  remove: async (id) => {
    const response = await httpClientLeadService.delete(`${BASE}/${id}`);
    return response.data;
  },

  /**
   * Fetch a recording as an object URL for <video src>.
   * The stream endpoint is JWT protected, so a plain URL cannot be used directly.
   */
  fetchObjectUrl: async (id) => {
    const response = await httpClientLeadService.get(`${BASE}/${id}/stream`, {
      responseType: 'blob',
      timeout: 0,
    });
    return URL.createObjectURL(response.data);
  },

  /** Absolute stream URL (for tools that can attach the bearer token themselves). */
  streamUrl: (id) => `${API_LEAD_SERVICE_CONFIG.BASE_LEAD_SERVICE_URL}${BASE}/${id}/stream`,

  /** Bearer token for the stream URL above. */
  authToken: () => tokenManager.getToken(),
};

export default screenRecordingService;
